<template>
  <div
    class="container-lg apartment-search-form"
    :class="classSize"
  >
    <div class="apartment-search-form__grid">

      <!-- Main -->
      <form
        class="apartment-search-form__main"
        @submit.prevent="submit"
        id="apartment-search-form-1001"
      >
        <div class="apartment-search-form__main-top">
          <div class="apartment-search-form__main-top-column-main">
            <!-- Location -->
            <div class="apartment-search-form__location">
              <div class="location-city-tag-wrapper">
              <InputWrap
                :wrap-label="false"
                v-slot="{id, error}"
                class="location-field-wrapper"
              >
                <MapPinIcon />
                <InputSelect
                  :id="id"
                  :options="locationOptions"
                  :internal-search="false"
                  :placeholder="translate('Paikkakunta, kaupunginosa, katu tai postinumero')"
                  :close-on-select="false"
                  track-by="value"
                  label="name"
                  data-cy="select-city"
                  @search-change="onLocationSearchTextChange"
                  @select="form.locations.push({...$event})"
                  hide-caret
                  :hide-dropdown="locationSearchText === null || locationSearchText.length < 2"
                  :error="error"
                  open-direction="bottom"
                >
                  <template #beforeList>
                    <div
                      v-if="locationOptionsLoading"
                      class="apartment-search-form__location-dropdown-loading"
                      :class="`apartment-search-form__location-dropdown-loading--${color}`"
                    >
                      <LoadSpinner :margin="false"/>
                    </div>
                    <div
                      class="apartment-search-form__location-dropdown-empty"
                      v-else-if="locationOptions.length === 0"
                    >
                      {{translate('Haulla ei löytynyt tuloksia.')}}
                    </div>
                  </template>
                </InputSelect>

              </InputWrap>

              <div
                v-if="form.locations.length > 0"
                class="apartment-search-form__location-tags"
              >
                <TagList
                  data-cy="city-tag-list"
                  :items="form.locations.map((location) => ({title: location.name, active: true}))"
                  @item-click="form.locations.splice($event, 1)"
                />
              </div>
            </div> <!-- location-city-tag-wrapper -->

            <!-- Modes/types of habitation -->
            <div class="apartment-search-form__filters-wrapper">

              <div class="apartment-search-form__filters-wrapper-modes-of-habitation">
                <!-- Filters -->
                <InputCheckbox
                  v-for="mode in modesOfHabitation"
                  v-bind:key="mode.value"
                  :value="mode.value"
                  :modelValue="form.booleanFilters.includes(mode.value) ||
                    preset ? (preset.type.includes(mode.value) ? 'checked' : false) : 'checked'"
                  @change="toggleSearchType(mode.value)"
                >
                <!-- TODO check how :modelValue status is set above (shouldn't use booleanFilters but form.type?). Does work like this? -->
                  {{ translate(mode.title) }}
                </InputCheckbox>
              </div>

            </div>

            <!-- end apartment-search-form__location -->
            </div>

          <!-- end apartment-search-form__main-top-column-main -->
          </div>

        </div><!-- .apartment-search-form__main-top -->

        <div
          v-if="showAll"
          class="apartment-search-form__hidden-fields"
        >

          <div class="row gx-5 gy-4">
            <div class="col-xl-8">
              <div class="apartment-search-form__dropdowns">

                <!-- Room amount selection -->
                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                    {{translate('Huoneet')}}
                    <span v-if="form.roomCounts.length > 0">({{ form.roomCounts.join('h, ') }}h)</span>
                  </button>
                  <div class="dropdown-menu p-4 apartment-search-form__dropdown--rooms">
                    <InputWrap
                      :label="translate('Huoneita')"
                      data-cy="rooms"
                      :wrap-label="false"
                    >
                      <div class="apartment-search-form__checkboxes apartment-search-form__checkboxes--rooms">
                        <InputCheckbox
                          autocomplete="off"
                          v-for="count in roomCounts"
                          v-bind:key="count.value"
                          :value="count.value"
                          v-model="form.roomCounts"
                          class="apartment-search-form__checkbox"
                        >
                          {{count.label}}h
                        </InputCheckbox>
                      </div>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                    {{translate('Pinta-ala')}}
                    <span v-if="shouldDisplayValue(form.minArea) || shouldDisplayValue(form.maxArea)">({{getFormattedNumberSpace(form.minArea, form.maxArea, 'm²')}})</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                      :label="translate('Pinta-ala')"
                      class=""
                    >
                    <div class="apartment-search-form__input--area">
                      <InputText
                        v-model.number="form.minArea"
                        data-cy="min-area"
                        type="number"
                        placeholder="Min"
                        min="0"
                      >
                      </InputText>
                      <span>—</span>
                      <InputText
                        v-model.number="form.maxArea"
                        data-cy="max-area"
                        type="number"
                        placeholder="Max"
                        min="0"
                      >
                      </InputText>
                      <span>m²</span>
                    </div>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                    {{translate('Talotyyppi')}}
                    <span v-if="form.buildingTypes.length > 0">({{ form.buildingTypes.join(', ') }})</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                    :label="translate('Talotyyppi')"
                    data-cy="building-type"
                    :wrap-label="false"
                    >
                      <div class="apartment-search-form__checkboxes d-flex flex-column">
                        <InputCheckbox
                          autocomplete="off"
                          v-for="type in combinedBuildingTypes()"
                          :key="type.value"
                          :value="type.value"
                          v-model="form.buildingTypes"
                          class="apartment-search-form__checkbox"
                        >
                          {{translate(type.label)}}
                        </InputCheckbox>
                      </div>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside"
                  :aria-disabled="!form.type.includes('owner-occupancy')">
                    {{translate('Velaton hinta')}}
                    <span v-if="shouldDisplayValue(form.minDebtFreePrice) || shouldDisplayValue(form.maxDebtFreePrice)">({{getFormattedNumberSpace(form.minDebtFreePrice, form.maxDebtFreePrice, '€')}})</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                      :label="translate('Velaton hinta')"
                      class=""
                    >
                      <div class="apartment-search-form__input--area apartment-search-form__input--debt-free-price">
                        <InputText
                        v-model.number="form.minDebtFreePrice"
                        data-cy="min-debt-free-price"
                        type="number"
                        :disabled="!form.type.includes('owner-occupancy')"
                        placeholder="Min"
                        min="1"
                        >
                        </InputText>
                        <span>€ </span>
                        <InputText
                          v-model.number="form.maxDebtFreePrice"
                          data-cy="max-debt-free-price"
                          type="number"
                          :disabled="!form.type.includes('owner-occupancy')"
                          placeholder="Max"
                          :min="form.minDebtFreePrice + 1"
                        >
                        </InputText>
                        <span>€</span>
                      </div>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside"
                  :aria-disabled="!form.type.includes('right-of-occupancy') && !form.type.includes('rental')">
                    {{ translate(maxPriceTitle('right-of-occupancy')) }}
                    <span v-if="shouldDisplayValue(form.maxPrice)">({{ form.maxPrice }}€)</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                      :label="translate(maxPriceTitle('right-of-occupancy'))"
                    >
                      <InputText
                        v-model.number="form.maxPrice"
                        data-cy="max-price"
                        type="number"
                        :disabled="!form.type.includes('right-of-occupancy') && !form.type.includes('rental')"
                        placeholder="Max"
                        min="0"
                      >€/kk
                      </InputText>
                    </InputWrap>
                  </div>

                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside"
                  :aria-disabled="!form.type.includes('right-of-occupancy')">
                    {{ translate('Luovutushinta') }}
                    <span v-if="shouldDisplayValue(form.maxTotalPrice)">({{ form.maxTotalPrice }}€)</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                      :label="translate('Max luovutushinta')"
                      class="apartment-search-form__total-price"
                    >
                      <InputText
                        v-model.number="form.maxTotalPrice"
                        type="number"
                        :disabled="!form.type.includes('right-of-occupancy')"
                        placeholder="Max"
                        min="0"
                      >€
                      </InputText>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                    {{ translate('Rakennusvuosi min-max') }}
                    <span v-if="shouldDisplayValue(form.yearMin) || shouldDisplayValue(form.yearMax)">({{getFormattedNumberSpace(form.yearMin, form.yearMax)}})</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputWrap
                      :label="translate('Rakennusvuosi min-max')"
                      :wrap-label="false"
                    >
                      <div class="apartment-search-form__input--area">
                        <InputText
                          class="apartment-search-form__joined-field"
                          v-model.number="form.yearMin"
                          placeholder="Min"
                          type="number"
                          min="0"
                        />
                        <span>—</span>
                        <InputText
                          class="apartment-search-form__joined-field"
                          v-model.number="form.yearMax"
                          placeholder="Max"
                          type="number"
                          min="0"
                        />
                      </div>
                    </InputWrap>
                  </div>
                </div>

                <div class="dropdown apartment-search-form__dropdown">
                  <button class="btn-multiselect dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
                    {{ translate('Vapautuu viimeistään') }}
                    <span v-if="form.availableBefore">({{ getFormattedDateString(form.availableBefore) }})</span>
                  </button>
                  <div class="dropdown-menu p-4">
                    <InputDate
                  v-model="form.availableBefore"
                  :placeholder="translate('Vapautuu viimeistään')"
                  :monday-first=true
                  format="dd.MM.yyyy"
                  :inline=true
                  :clear-button=true
                ></InputDate>
                  </div>
                </div>

              </div>
            </div>
            <div class="col-xl-4">
              <div class="row g-4">
                <div class="col-12 col-sm-6">

                  <h2 class="apartment-search-form__subtitle">{{translate('Tila')}}</h2>

                  <div class="form-input-wrapper">

                    <InputCheckbox
                      value="available"
                      autocomplete="off"
                      :modelValue="form.booleanFilters.includes('available')"
                      @change="toggleBooleanFilter('available')"
                    >
                      {{translate('Heti vapaa')}}
                    </InputCheckbox>

                    <InputCheckbox
                      value="new"
                      autocomplete="off"
                      :modelValue="form.booleanFilters.includes('new')"
                      @change="toggleBooleanFilter('new')"
                    >
                      {{translate('Vain uudiskohteet')}}
                    </InputCheckbox>

                    <InputCheckbox
                      value="showing"
                      autocomplete="off"
                      :modelValue="form.booleanFilters.includes('showing')"
                      @change="toggleBooleanFilter('showing')"
                    >
                      {{translate('Vain esittelyssä olevat asunnot')}}
                    </InputCheckbox>

                    <InputCheckbox
                      value="campaign"
                      :modelValue="form.booleanFilters.includes('campaign') || show_campaign === true"
                      @change="toggleBooleanFilter('campaign')"
                    >
                      {{ translate('Vain kampanja-asunnot') }}
                    </InputCheckbox>

                  </div>

                </div>
                <div class="col-12 col-sm-6">
                  <h2 class="apartment-search-form__subtitle">{{translate('Ominaisuudet')}}</h2>

                  <div class="form-input-wrapper apartment-search-form__boolean-filters">

                    <InputCheckbox
                      autocomplete="off"
                      v-for="type in drawerBooleanFilterOptions"
                      :key="type.value"
                      :value="type.value"
                      v-model="form.booleanFilters"
                      class=""
                    >
                      {{translate(type.title)}}
                    </InputCheckbox>

                  </div>
                </div>
              </div>
            </div>
          </div>

        </div><!-- .apartment-search-form__hidden-fields -->

        <div class="apartment-search-form__main-bottom">
          <button
            type="submit"
            class="btn btn--large location-search-button"
            id="apartment-search-form-button-2001"
          >
            <SearchIcon />
            {{translate('Hae asuntoja')}}
          </button>

          <!-- "Lisää hakuehtoja" -->
          <div class="apartment-search-form__toggle-more-container">
            <button
              type="button"
              class="apartment-search-form__toggle-more"
              @click="showAll = !showAll"
            >
              {{showAll ? translate('Piilota hakuehdot') : translate('Lisää hakuehtoja')}}
            <MinusIcon
              v-if="showAll"
              class="apartment-search-form__toggle-more-icon"/>
            <PlusIcon
              v-else
              class="apartment-search-form__toggle-more-icon"/>
            </button>
          </div>
        </div><!-- .apartment-search-form__main-bottom -->

      </form>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import {debounce} from 'debounce';
  import { useVuelidate } from '@vuelidate/core';
  import globals from '../../lib/globals';
  import utils from '../mixins/utils';
  import InputWrap from './InputWrap';
  import InputText from './InputText';
  import TagList from './TagList';
  import InputCheckbox from './InputCheckbox';
  import InputSelect from './InputSelect';
  import InputDate from './InputDate';
  import LoadSpinner from './LoadSpinner';
  import MinusIcon from '../icons/MinusIcon.vue';
  import PlusIcon from '../icons/PlusIcon.vue';

  // Icons.
  import MapPinIcon from '../icons/MapPinIcon.vue';
  import SearchIcon from '../icons/SearchIcon2.vue';

  export default {
    components: {
      LoadSpinner,
      InputCheckbox,
      TagList,
      InputWrap,
      InputText,
      InputSelect,
      InputDate,
      MinusIcon,
      PlusIcon,
      MapPinIcon,
      SearchIcon
    },
    mixins: [
      utils.translate,
    ],
    setup() {
      return { v$: useVuelidate() };
    },
    validations() {
      return {};
    },
    props: {
      preset: {
        type: Object,
        default: null,
      },
      size: {
        type: String,
        default: 'full',
      },
      show_campaign: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      const preset = this.preset ? this.preset : {};
      const show_campaign = this.show_campaign ? this.show_campaign : false;
      const form = {
        // TODO check do these set correctly when coming from standalone form!!
        type: ['right-of-occupancy', 'rental', 'owner-occupancy'],
        locations: [],
        minArea: '',
        maxArea: '',
        maxPrice: '',
        maxTotalPrice: '',
        minDebtFreePrice: '',
        maxDebtFreePrice: '',
        availableBefore: null,
        roomCounts: [],
        buildingTypes: [],
        booleanFilters: [],
        yearMin: null,
        yearMax: null,
        ...preset,
      };

      const yearNow = new Date().getFullYear();
      const optionsYearBase = [...Array(150).keys()].map(i => {
        const year = yearNow + 1 - i;
        return {
          label: year,
          value: year,
        };
      });

      const drawerBooleanFilterOptionsBase = [
        /*
        {
          title: 'Heti vapaa',
          value: 'available',
          type: 'all',
        },
        {
          title: 'Vain uudiskohteet',
          value: 'new',
          type: 'all',
        },
        */
        {
          title: 'Huoneistosauna',
          value: 'sauna',
          type: 'all',
        },
        {
          title: 'Hissi',
          value: 'elevator',
          type: 'all',
        },
        {
          title: 'Talosauna',
          value: 'housing_locality_sauna',
          type: 'all',
        },
        {
          title: 'Pesutupa',
          value: 'laundry_room',
          type: 'all',
        },
        {
          title: 'Savuton kohde',
          value: 'non_smoking',
          type: 'all',
        },
        {
          title: 'Parveke',
          value: 'balcony',
          type: 'all',
        },
      ];

      const activeDrawerBooleanFilters = form.booleanFilters
        .filter(filter => drawerBooleanFilterOptionsBase.map(f => f.value).includes(filter));
      return {
        optionsYearBase,
        locationSearchText: null,
        locationOptionsLoading: false,
        locationOptions: [],
        modesOfHabitation: [
          {
            title: 'Omistusasunnot',
            value: 'owner-occupancy',
          },
          {
            title: 'ASO-asunnot',
            value: 'right-of-occupancy',
          },
          {
            title: 'Vuokra-asunnot',
            value: 'rental',
          }
        ],
        roomCounts: [...[...Array(4).keys()]
          .map((index) => ({
            label: (index + 1).toString(),
            value: index + 1,
          })), {
          label: '5+',
          value: 5,
        }],
        buildingTypes: {
          'right-of-occupancy': [],
          'rental': [],
          'owner-occupancy': [],
        },
        drawerBooleanFilterOptionsBase,
        showAll: (
          // TODO: Change to ">= 0" when you want the filters drawer to be always open.
          // TODO: Change to "> 0" when you want the filters drawer to be hidden by default.
          activeDrawerBooleanFilters.length > 0
          ||
          form.availableBefore
          ||
          form.yearMax
          ||
          form.yearMin
          ||
          form.minDebtFreePrice
          ||
          form.maxDebtFreePrice
        ),
        form,
      };
    },
    mounted() {
      this.checkApartments();
      this.getBuildingTypes();

      if (this.show_campaign) {
        this.toggleBooleanFilter('campaign');
      }
    },
    computed: {
      drawerBooleanFilterOptions() {
        return this.drawerBooleanFilterOptionsBase.map((option) => ({
          ...option,
          title: option.title,
          active: this.form.booleanFilters.includes(option.value),
        }));
      },
      optionsYearMin() {
        if (!this.form.yearMax) {
          return this.optionsYearBase;
        }
        const indexMax = this.optionsYearBase.findIndex(option => option.value === this.form.yearMax);
        return this.optionsYearBase.map((option, index) => ({
          ...option,
          $isDisabled: index < indexMax,
        }));
      },
      optionsYearMax() {
        if (!this.form.yearMin) {
          return this.optionsYearBase;
        }
        const indexMin = this.optionsYearBase.findIndex(option => option.value === this.form.yearMin);
        return this.optionsYearBase.map((option, index) => ({
          ...option,
          $isDisabled: index > indexMin,
        }));
      },
      classSize() {
        return {
          'apartment-search-form--card-wide': this.size === 'card-wide',
          'apartment-search-form--full': this.size === 'full',
        };
      },
    },
    methods: {
      maxPriceTitle(type) {
        return {
          rental: 'Max vuokra',
          'right-of-occupancy': 'Vuokra/ASO-vastike',
          'owner-occupancy': 'Max vastike',
        }[type];
      },
      onLocationSearchTextChange(searchText) {
        this.locationOptions = [];
        this.locationSearchText = searchText;

        if (searchText.length < 2) {
          return;
        }

        this.locationOptionsLoading = true;
        this.requestLocationsLazy(searchText);
      },
      requestLocationsLazy: debounce(async function (searchText) {
        let searchType = this.form.type;
        const url = `${globals.urlIntegrationApi}/public/location-filters-all-types-search?s=${encodeURIComponent(searchText)}&t=${searchType}`;
        const response = await axios.get(url);

        // Bail out if the search text has been changed since
        if (this.locationSearchText !== searchText) {
          return;
        }

        this.locationOptionsLoading = false;
        this.locationOptions = response.data
          .filter((item) => !this.form.locations.map(i => i.value).includes(item.value))

      }, 500),
      checkApartments: debounce(async function() {
        // Sets housing types which have apartments (right-of-occupancy, rental, owner-occupancy)
        const url = `${globals.urlIntegrationApi}/public/apartment-check`;
        const response = await axios.get(url);
        if(response.status != 200) {
          return false;
        }

        if(!response.hasOwnProperty('data') || response.data == null || response.data.length <= 0 || !response.data.hasOwnProperty('apartmentsFoundForType')) {
          return false;
        }

        this.showSearchFormTab = response.data.apartmentsFoundForType;
        return true;
      }, 100),
      getBuildingTypes: debounce(async function() {
        // Get building types for different habitation modes (right-of-occupancy, rental, owner-occupancy)
        const url = `${globals.urlIntegrationApi}/public/building-types`;
        const response = await axios.get(url);
        if(response.status != 200) {
          return false;
        }

        if(!response.hasOwnProperty('data') || response.data == null || response.data.length <= 0 || !response.data.hasOwnProperty('buildingTypes')) {
          return false;
        }

        const buildingTypesTmp = response.data.buildingTypes;
        for (var searchType in buildingTypesTmp) {
          this.buildingTypes[searchType] = buildingTypesTmp[searchType].map((type) => ({
            label: type,
            value: type,
          }));
        }

        return true;
      }, 100),
      combinedBuildingTypes() {
        /*
         *const filteredBT = [Object.keys(this.buildingTypes).filter((key) => {
         *    return this.form.type.includes(key)
         *  })
         *  .reduce((acc, key) => {
         *    acc = { ...acc, ...this.buildingTypes[key] };
         *    return acc;
         *  }, {})];
         *console.log(filteredBT);
         *const combinedBT = filteredBT.reduce((acc, cur) => {
         *  return { ...acc, ...cur };
         *},  {});
         *console.log(combinedBT);
         */
        // TODO fix building types combination, fake it for now
        const combinedBT = [
              'Erillistalo',
              'Luhtitalo',
              'Kerrostalo',
              'Rivitalo',
              'Paritalo',
            ].map((type) => ({
              label: type,
              value: type,
            }));
        return combinedBT;
      },
      onDrawerBooleanFilterClick(index) {
        const filterClicked = this.drawerBooleanFilterOptions[index].value;
        this.toggleBooleanFilter(filterClicked);
      },
      toggleSearchType(type) {
        const typeIndex = this.form.type.findIndex(t => t === type);
        if (typeIndex === -1) {
          this.form.type.push(type);
        } else {
          this.form.type.splice(typeIndex, 1);
        }
      },
      toggleBooleanFilter(filter) {
        const filterIndex = this.form.booleanFilters.findIndex(f => f === filter);
        if (filterIndex === -1) {
          this.form.booleanFilters.push(filter);
        } else {
          this.form.booleanFilters.splice(filterIndex, 1);
        }
      },
      submit() {
        const formCopy = JSON.parse(JSON.stringify(this.form));

        const stateString = encodeURIComponent(JSON.stringify(formCopy));
        this.$emit('submit-all-types', formCopy, stateString);
        window.history.pushState('', '', `?q=${stateString}`);
      },
      getFormattedNumberSpace(min, max, suffix) {
        var output = '';
        if (!suffix) {
          var suffix = '';
        }
        if (min !== null && min !== undefined && min !== '' && min >= 0) {
          output += min + suffix;
        }
        output += ' — ';
        if (max !== null && max !== undefined && max !== '' && max >= 0) {
          output += max + suffix;
        }
        return output;
      },
      getFormattedDateString(datetime) {
        let date = new Date(datetime);

        var dateString = date.getDate() + '.' + ( date.getMonth() + 1 ) + '.' + date.getFullYear();

        return dateString;
      },
      shouldDisplayValue(value) {
        return value !== null && value !== undefined && value !== '' && value >= 0;
      }
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  @mixin apartment-search-form__medium {
    .apartment-search-form--card-wide & {
      @include media-breakpoint-up(xl) {
        @content;
      }
    }

    .apartment-search-form--full & {
      @include media-breakpoint-up(md) {
        @content;
      }
    }
  }

  @mixin apartment-search-form__large {
    .apartment-search-form--full & {
      @include media-breakpoint-up(xl) {
        @content;
      }
    }
  }

  @mixin apartment-search-form__main-top-column() {
    margin-bottom: r(16);
  }

  .apartment-search-form {
    @include card;
    background-color: $color-pink-light-2025;
    color: $color-green-2025;

    .input-text__input {
      font-weight: 700;
      border: solid r(1) $color-primary;

      &:disabled {
        background-color: #e0e0e0;
        color: $color-dark-gray;
      }
    }
    .input-wrap__label {
      font-weight: 400;
    }

    .location-search-button {
      padding: r(16);
      border-radius: 3em;
      top: r(1);
      background-color: $color-pink-2025;
      color: $color-green-2025;
      font-size: r(16);
      line-height: r(24);
      padding: r(18) r(80);
      transition: $button-transition;

      display: flex;
      width: max-content;
      white-space: nowrap;
      flex-wrap: nowrap;
      align-items: center;
      min-height: unset;

      &:hover {
        background-color: $color-pink-2025-hover;
      }

      .search {
        width: 1.5em;
        height: 1.5em;
      }

      @include media-breakpoint-down(sm) {
        flex-basis: 100%;
      }
    }
  }

  .apartment-search-form__grid {
    padding: 0;

    @include apartment-search-form__medium {
      display: flex;
      padding: 0 0;
    }

    @include apartment-search-form__large {
      padding: 0;
    }
  }

  .apartment-search-form__sidebar {
    margin-bottom: r(24);

    @include apartment-search-form__medium {
      width: r(160);
      padding-top: r(32);
      padding-right: r(24);
      border-right: r(1) solid #d8d8d8;
      margin-right: r(16);
      margin-bottom: 0;
    }

    @include apartment-search-form__large {
      width: r(280);
      padding-right: r(48);
      padding-top: r(40);
      margin-right: r(40);
    }
  }

  .apartment-search-form__title {
    @include font-h-28;
    margin-bottom: r(24);
    text-align: center;

    @include apartment-search-form__medium {
      margin-bottom: r(40);
      text-align: left;
    }

    @include apartment-search-form__large {
      @include font-h-42;
    }
  }

  .apartment-search-form__main {
    padding-top: r(24) !important;
    padding-bottom: r(24) !important;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .apartment-search-form__main-top {
    @include apartment-search-form__medium {
      margin-bottom: 0;
    }

    @include apartment-search-form__large {
      display: flex;
      justify-content: space-between;
      flex-flow: row wrap;
      align-items: flex-start;
      gap: 10px 30px;
    }
  }

  .apartment-search-form__main-top-column-main {
    @include apartment-search-form__main-top-column;
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0;

    @include apartment-search-form__large {
      flex: 0 1;
      min-width: 100%;
      max-width: 100%;
    }
  }

  .apartment-search-form__location {
    margin-bottom: r(16);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0 !important;

    @include apartment-search-form__medium {
      flex-wrap: wrap;
    }

    .location-city-tag-wrapper {
      flex: 1;
      min-width: 11rem;

      @include media-breakpoint-down(md) {
        width: 100%;
      }

      @include media-breakpoint-up(md) {
        min-width: 20rem;
      }

      @include media-breakpoint-up(xl) {
        min-width: 30rem;
        max-width: 40rem;
      }

      .location-field-wrapper .tag-list__grid .tag-list__tag .btn--round {
        padding: r(5) r(16);
      }
    }
    .location-field-wrapper {

      > div:not([class]) {
        position: relative;
      }

      .map-pin {
        position: absolute;
        top: 50%;
        left: .75rem;
        color: $color-black;
        z-index: 51;
        transform: translateY(-50%);
      }
    }

    .multiselect {
      width: 100%;
    }
    .multiselect__tags {
      border: none;
      padding: r(8) r(16) r(8) r(44);
      border-radius: 3em;
      height: 3.5em;

      &:has(input:focus-visible) {
        outline: 2px solid $color-black;
        outline-offset: -2px
      }
    }
    .multiselect__content-wrapper {
      margin-left: 25px;
      margin-right: 25px;
      border: none;
    }
    .apartment-search-form__filters-wrapper {
      display: flex;
      flex-flow: wrap;

      @include media-breakpoint-up(xl) {

        .input-wrapper {
          min-height: 4rem;
        }
      }

      gap: r(16);
      font-size: r(16);
      align-self: flex-start;
      align-items: center;

      @media only screen and (min-width: 941px) {
        min-height: calc( 3.5em * 18 / 16 );
      }

      @include media-breakpoint-down(md) {
        flex-wrap: wrap;
      }

      .input-wrapper {
        display: flex;
        min-height: 3rem;
        flex-grow: 1;
      }

      /* Custom checkbox */
      input[type="checkbox"] {
        position: absolute;
        top: auto;
        overflow: hidden;
        clip: rect(1px, 1px, 1px, 1px);
        width: 1px;
        height: 1px;
        white-space: nowrap;
      }

      input[type=checkbox] + label {
        display: block;
        padding: .25em .5em;
        max-width: unset;
        position: relative;
        white-space: nowrap;
        line-height: 2em;
        align-self: center;
      }

      input[type="checkbox"] + label::before {
        content: '';
        border: .15em solid $color-green-2025;
        background-color: transparent;
        border-radius: 3em;
        display: block;
        box-sizing: border-box;
        float: left;
        width: r(24);
        height: r(24);
        vertical-align: middle;
        cursor: pointer;
        text-align: center;
        transition: all .1s ease-out;
        position: absolute;
        left: -1.8em;
        top: 50%;
        transform: translateY(-50%);
      }

      input[type="checkbox"] + label::after {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-59%) rotate(45deg);
        left: -1.3em;
        width: r(24);
        height: r(24);
        transition: border .1s ease-out;
      }

      input[type="checkbox"]:checked + label::before {
        width: r(24);
        height: r(24);
        background-color: $color-green-2025;
        border: 0;
        position: absolute;
        left: -1.8em;
      }

      input[type="checkbox"]:checked + label::after {
        //top: 0.9em;
        left: -1.3em;
        width: 0.5em;
        height: 1em;
        border-right: r(2) solid $color-white;
        border-bottom: r(2) solid $color-white;
      }

      @include apartment-search-form__large {
        margin-top: 0;
        margin-bottom: 0;
      }

    }

    .apartment-search-form__filters-wrapper-modes-of-habitation {
      display: flex;
      flex-flow: wrap;
      flex-grow: 1;
      column-gap: 1rem;

      .form-check:has(input:focus-visible) {
        outline: 2px solid $color-black;
        outline-offset: -2px;
        border-radius: 5px;
      }

      @include media-breakpoint-up(xl) {
        flex-grow: none;
      }
    }

    @include apartment-search-form__large {
      margin-bottom: r(40);
    }
  }

  .apartment-search-form__location-dropdown-loading {
    padding: r(12) 0;
    color: $color-pink-2025;

    &--yellow {
      color: $color-yellow;
    }
  }

  .apartment-search-form__location-dropdown-empty {
    padding: r(8);
  }

  .apartment-search-form__location-tags {
    margin-top: r(10);
  }

  .apartment-search-form__double-field {
    display: flex;
    margin: 0 r(-10);
  }

   .apartment-search-form__double-field--blue--first {
    margin: 0 r(-10) r(16);
  }

  .apartment-search-form__double-field-column {
    width: 50%;
    padding: 0 r(10);
  }

  .apartment-search-form__total-price {
    margin-top: 0;
  }

  .apartment-search-form__main-top-column-side {
    @include apartment-search-form__main-top-column;

    @include apartment-search-form__large {
      flex-grow: 1;
      max-width: r(264);
    }
  }

  .apartment-search-form__grid-checkboxes {
    @include apartment-search-form__large {
      display: flex;
      justify-content: space-between;
      margin-left: r(6);
      margin-right: r(6);
    }
  }

  .apartment-search-form__grid-checkboxes-column {
    margin-bottom: r(16);

    @include apartment-search-form__large {
      margin: 0 r(16);
    }
  }

  .apartment-search-form__checkboxes {

    @include apartment-search-form__medium {
      margin: r(-7) r(-10);
    }

    @include apartment-search-form__large {
      padding-top: r(4);
      flex-direction: column;
      margin: 0 0 r(-8);
    }
  }

  .apartment-search-form__dropdown--rooms {
    min-width: 20rem;
  }

  .apartment-search-form__checkboxes--rooms {

    .apartment-search-form--full & {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: .5rem;
      margin: 0;

      @include media-breakpoint-down(sm) {
        flex-wrap: wrap;
      }
    }

    .form-check.form-check {
      display: inline-flex;
      padding-left: 0;
      margin: 0;
    }

    .form-check-input {
      position: absolute;
      top: auto;
      overflow: hidden;
      clip: rect(1px, 1px, 1px, 1px);
      width: 1px;
      height: 1px;
      white-space: nowrap;
    }

    .form-check-label {
      border: 1px solid $color-primary;
      padding: r(6) r(24);
      width: unset;
      font-weight: 700;
      font-size: 1.125rem;
      line-height: r(28);

      &:hover {
        cursor: pointer;
      }
    }

    .form-check-input:checked + .form-check-label {
      background-color: $color-primary;
      color: $color-white;
    }

    .form-check-input:focus-visible + .form-check-label {
        outline: 2px solid #101010;
        outline-offset: 2px;
        border-radius: 3px;
      }
  }

  .apartment-search-form__checkbox {
    // margin-top: r(4);
    // margin-bottom: 0;

    @include apartment-search-form__medium {
      margin-top: r(7);
      margin-bottom: 0;
    }

    @include apartment-search-form__large {
      margin-top: r(8);
      margin-bottom: 0;
    }
  }

  .apartment-search-form__showing-checkbox {
    margin-top: r(16);
    padding-top: 0;
    border-top: none;
  }
  .apartment-search-form__own-plot-checkbox {
    margin-top: r(16);
  }

  .apartment-search-form__campaign-checkbox {
    margin-top: r(16);
    font-weight: normal;
  }

  .apartment-search-form__main-middle {
    margin-bottom: r(16);

    @include apartment-search-form__medium {
      margin-bottom: r(4);
    }
  }

  .apartment-search-form__main-bottom {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    align-items: center;
  }

  .apartment-search-form__toggle-more-container {
    position: relative;
    align-items: center;
    justify-content: center;
    width: max-content;
    margin-right: auto;

    @include apartment-search-form__medium {
      justify-content: flex-end;
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      height: r(1);
      background-color: transparent;
    }
  }

  .apartment-search-form__toggle-more {
    @include reset-button(true);
    font-size: r(16);
    position: relative;
    display: flex;
    align-items: center;
    line-height: calc(15 / 12);
    padding: 0 r(8);
    background-color: transparent;
    // margin-bottom: r(16);

    &:focus {
      text-decoration: underline;
    }

    @include apartment-search-form__large {
      font-size: r(16);
      // top: r(11);
    }
  }

  $apartment_search_icon_size: 1.5rem;

  .apartment-search-form__toggle-more-icon {
    width: $apartment_search_icon_size;
    height: $apartment_search_icon_size;
    flex-shrink: 0;
    overflow: hidden;
    margin-left: $theme-spacing * 0.5;
    background-color: transparent;
    color: $color-green-2025;

    svg {
      width: $apartment_search_icon_size;
      height: $apartment_search_icon_size;
      fill: currentColor;
    }
  }

  .apartment-search-form__hidden-fields {
    margin-top: 0;
    margin-bottom: r(12);
    font-size: r(16);

    .input-wrap__label {
      font-size: r(16);
    }

    @include apartment-search-form__medium {
      width: 100%;
      margin-top: r(8);
      margin-bottom: r(12);
    }
    .tag-list {
      .tag-list__tag:has(> .tag-list__icon--active) {
        background-color: $color-green-light-2025;
      }
    }
  }

  .apartment-search-form__boolean-filters {
    margin-top: r(16);
  }

  .apartment-search-form__bottom-filters-title {
    @include font-label-16;
    margin: r(40) 0 r(24);
  }

  .apartment-search-form__bottom-filters {
    margin: r(16) r(-8) r(-16);

    @include apartment-search-form__large {
      display: flex;
    }
  }

  .apartment-search-form__bottom-filter-col {
    padding: 0 r(8) r(16);

    @include apartment-search-form__large {
      width: 50%;
    }
  }

  .apartment-search-form__joined-fields {
    display: flex;
    margin: 0 r(-4);
  }

  .apartment-search-form__joined-field {
    width: 50%;
    margin: 0 r(4);
  }

  .select-year-of-building {
    .multiselect__tags {
      border: none;
    }
  }

  .apartment-search-form__subtitle {
    font-family: $ff-lato;
    font-size: r(16);
    line-height: r(20);
    //font-weight: 500;
    margin-bottom: 1rem;
  }

  .apartment-search-form__dropdown {
    .input-wrap__label {
      font-size: 1.125rem;
      line-height: r(28);
      font-weight: 700;
    }
  }

  .apartment-search-form__input--area {
    display: flex;
    flex-wrap: nowrap;
    gap: .25rem;
    align-items: center;

    span:not([class]) {
      font-weight: 700;
    }

    &.apartment-search-form__input--debt-free-price input {
      min-width: 10ch;
    }
  }

  .apartment-search-form .input-date {
    .input-date__clear {
      display: none;
    }
    &.input-text {
      background-color: $color-white;
    }
    .vdp-datepicker__clear-button i span {
      position: relative;
      &:after {
        content: "Tyhjennä";
        margin-left: .5rem;
      }
    }
    .cell.selected {
      background-color: $color-primary;
      color: $color-white;
      &:hover {
        background-color: $color-primary;
      }
    }
    .cell.today {
      background-color: $color-green-lighter-2025;
    }
    .cell:not(.blank):not(.disabled).day:hover {
      border: 1px solid $color-primary;
    }
  }

  .apartment-search-form__dropdowns {
    display: grid;
    gap: 1rem 2rem;
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-template-rows: auto;
    grid-auto-columns: unset;

    @include media-breakpoint-up(md) {
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      grid-template-rows: repeat(4, auto); /* How many rows max = 4 */
    }
  }
</style>
